import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FeatureCollection } from 'geojson';
import { Feature } from 'maplibre-gl';
import { BehaviorSubject, Observable } from 'rxjs';

import { MAPTILER_KEY } from 'src/app/shared/app-token';
import { Unlocode } from 'src/app/types/unlocode';

/**
 * geolocation service
 * provides static unlocode json data
 * @class GeoLocationService
 * @typedef {GeoLocationService}
 */
@Injectable( {
    providedIn: 'root'
} )
export class GeoLocationService {
    /** ignore in coverage */
    #httpClient = inject( HttpClient );
    #maptiler = inject( MAPTILER_KEY );

    #locationCodes = new Set( [] as Feature[] );
    #geoLocationsFeatures: Feature[] = [];

    /** maplibre-gl {@link FeatureCollection} */
    #geoLocationsCollection = {
        type: 'FeatureCollection',
        features: this.#geoLocationsFeatures
    };

    /** observable object to be returned to {@link geoLocationResolver} */
    private geoLocationSubject = new BehaviorSubject( this.#geoLocationsCollection );
    readonly geoLocationSource$ = this.geoLocationSubject.asObservable();

    /**
     * create geolocation structure as needed by map
     * updates {@link #geoLocationsFeatures} observable
     * @param data which includes data
     * @returns GeoJson object
     */
    createGeoLocationJson( data: Unlocode[] ) {
        data.forEach( ( loc: Unlocode ) => {
            if ( loc.longitude && loc.latitude && ( loc['airport'] || loc['port'] || loc['railTerminal'] ) ) {
                const label_ports = $localize`:@@unlocode-label-ports:Ports at this location`;
                const label_air = $localize`:@@unlocode-port-airport:Airport`;
                const label_sea = $localize`:@@unlocode-port-port:Harbour`;
                const label_rail = $localize`:@@unlocode-port-rail:Rail terminal`;

                const air_icon = loc['airport']
                    ? `<div class="icon-item">
                    <span class="icon-img riege-icon material-icons md-16" aria-hidden="true">flight</span><div>${label_air}</div>
                </div>`
                    : '';
                const sea_icon = loc['port']
                    ? `<div class="icon-item">
                    <span class="icon-img riege-icon material-icons md-16" aria-hidden="true">directions_boat</span><div>${label_sea}</div>
                </div>`
                    : '';
                const rail_icon = loc['railTerminal']
                    ? `<div class="icon-item">
                    <span class="icon-img riege-icon material-icons md-16" aria-hidden="true">directions_railway</span><div>${label_rail}</div>
                </div>` : '';

                // reduced whitespace in resulting object
                const markerPopupTemplate = `<div class="community-map-marker-popup">
                    <div class="community-map-marker-popup__card-header">
                        <div class="headline">${loc['name']}</div>
                        <div class="subline">${loc['unlocode']}</div>
                    </div>
                ${loc['airport'] || loc['port'] || loc['railTerminal']
                        ? `
                    <div class="community-map-marker-popup__card-content">
                        <div class="heading">${label_ports}</div>
                        <div class="icon-wrapper">
                            ${air_icon}
                            ${sea_icon}
                            ${rail_icon}
                        </div>
                    </div>` : ''}
                </div>`;

                const feature = {
                    id: loc.unlocode,
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [loc.longitude, loc.latitude]
                    },
                    properties: {
                        id: loc.unlocode,
                        code: loc.unlocode,
                        name: loc.name,
                        description: markerPopupTemplate,
                        airport: loc.airport,
                        railTerminal: loc.railTerminal,
                        port: loc.port
                    }
                } as unknown as Feature;

                this.#locationCodes.add( feature );
            }
        } );

        this.#geoLocationsFeatures = [...this.#locationCodes];
        this.#geoLocationsCollection.features = this.#geoLocationsFeatures;
        this.geoLocationSubject.next( this.#geoLocationsCollection );
        return this.#geoLocationsCollection;
    }

    clearDataSource() {
        this.#locationCodes.clear();
    }

    /**
     * helper function for resolver
     * @param data any kind of data
     * @returns observed {@link FeatureCollection} object
     */
    getGeoLocations( data: any ) {
        this.#locationCodes.clear();
        return this.createGeoLocationJson( data );
    }

    /**
     * helper function to use maptiler forward geocoding api
     * @param query string to search a location via maptiler geocoding api
     * @param queryParameters additional parameters to refine the search
     * @returns {Array} list of search hits
     */
    searchByName( query: string, queryParameters?: any ): Observable<FeatureCollection> {
        const apiKey = `key=${this.#maptiler}`;
        return this.#httpClient.get<FeatureCollection>(
            `https://api.maptiler.com/geocoding/${query}.json?${queryParameters ? queryParameters + '&' : ''}${apiKey}`
        );
    }
}
